import { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection, doc, addDoc, getDocs, query, where, serverTimestamp,
} from "firebase/firestore";
import { IC, Avatar, Stars, useToast } from "../shared";

const TWO_HOURS = 2 * 60 * 60 * 1000;

const ForgotItem = ({ currentUser, onBack, onOpenLostChat }) => {
  const toast = useToast();
  const [recentDrivers, setRecentDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null); // requestId being created
  const [, tick] = useState(0);

  // Refresh time-remaining every second for live countdown
  useEffect(() => {
    const iv = setInterval(() => tick(x => x + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const now = new Date();
      const twoHoursAgo = new Date(now.getTime() - TWO_HOURS);

      // Get accepted requests as passenger where trip is completed (past) but within 2 hours
      const snap = await getDocs(
        query(collection(db, "requests"),
          where("passengerId", "==", currentUser.id),
          where("status", "==", "accepted"))
      );

      const eligible = [];
      for (const d of snap.docs) {
        const r = { id: d.id, ...d.data() };
        const tripDt = new Date(r.tripDatetime);
        if (tripDt > now) continue; // not completed yet
        if (tripDt < twoHoursAgo) continue; // more than 2 hours ago
        eligible.push(r);
      }

      // Check if a lost-item chat already exists for each
      const withStatus = [];
      for (const r of eligible) {
        const existingSnap = await getDocs(
          query(collection(db, "lostItemChats"),
            where("requestId", "==", r.id),
            where("passengerId", "==", currentUser.id))
        );
        const existing = existingSnap.docs[0];
        withStatus.push({
          ...r,
          existingChatId: existing?.id || null,
          chatCreatedAt: existing?.data()?.createdAt?.toDate?.() || null,
        });
      }

      // Sort by most recent trip first
      withStatus.sort((a, b) => new Date(b.tripDatetime) - new Date(a.tripDatetime));
      setRecentDrivers(withStatus);
      setLoading(false);
    })();
  }, [currentUser.id]);

  const timeRemaining = (tripDatetime) => {
    const elapsed = Date.now() - new Date(tripDatetime).getTime();
    const remaining = TWO_HOURS - elapsed;
    if (remaining <= 0) return null;
    const mins = Math.floor(remaining / 60000);
    const hrs = Math.floor(mins / 60);
    const m = mins % 60;
    return hrs > 0 ? `${hrs}h ${m}m left` : `${m}m left`;
  };

  const completedAgo = (tripDatetime) => {
    const elapsed = Date.now() - new Date(tripDatetime).getTime();
    const mins = Math.floor(elapsed / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
  };

  const contactDriver = async (req) => {
    if (creating) return;
    setCreating(req.id);
    try {
      // Check if chat already exists
      if (req.existingChatId) {
        onOpenLostChat({ chatId: req.existingChatId, ...req });
        setCreating(null);
        return;
      }

      // Create the lost-item chat document
      const chatRef = await addDoc(collection(db, "lostItemChats"), {
        passengerId: currentUser.id,
        passengerName: currentUser.name,
        passengerAvatar: currentUser.avatar || null,
        driverId: req.driverId,
        driverName: req.driverName,
        driverAvatar: req.driverAvatar || null,
        tripId: req.tripId,
        requestId: req.id,
        tripFrom: req.tripFrom,
        tripTo: req.tripTo,
        tripDatetime: req.tripDatetime,
        createdAt: serverTimestamp(),
        status: "active",
      });

      // Notify the driver
      await addDoc(collection(db, "notifications", req.driverId, "items"), {
        text: `${currentUser.name} may have left an item in your car (${req.tripFrom} → ${req.tripTo}). Tap to chat.`,
        tab: "chat", type: "lost_item", chatId: chatRef.id,
        ts: serverTimestamp(), read: false,
      });

      onOpenLostChat({ chatId: chatRef.id, ...req });
    } catch (e) {
      console.error("Failed to create lost-item chat:", e);
      toast("Failed to contact driver. Please try again.", "error");
    }
    setCreating(null);
  };

  return (
    <div className="screen aslide" style={{ padding: "0 0 90px", overflowY: "auto" }}>
      <div className="desk-container-sm" style={{ padding: 0 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 16px 0" }}>
          <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 12, background: "var(--warm)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
            <span style={{ width: 18, height: 18, color: "var(--ink)" }}>{IC.back}</span>
          </button>
          <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--ink)", margin: 0 }}>Forgot an Item</h2>
        </div>

        {/* Info banner */}
        <div style={{ margin: "16px 16px 0", padding: "14px 16px", background: "rgba(245,166,35,.08)", borderRadius: 14, border: "1px solid rgba(245,166,35,.2)", display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(245,166,35,.15)", color: "#f5a623", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.45 }}>
            <strong style={{ fontFamily: "Syne", fontWeight: 700 }}>Contact your recent driver</strong><br/>
            You can open a temporary chat within <strong>2 hours</strong> after your trip ends. The chat will stay open for <strong>12 hours</strong>.
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "var(--muted)" }}>
            <span style={{ width: 24, height: 24, border: "3px solid var(--accent)", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/>
          </div>
        ) : recentDrivers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 30px", color: "var(--muted)" }}>
            <div style={{ width: 64, height: 64, margin: "0 auto 16px", background: "var(--warm)", borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--muted)" }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 17, color: "var(--ink)", marginBottom: 6 }}>No recent trips</div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>You can only contact a driver within 2 hours after your trip ends. If you need further help, please contact iGo support.</div>
          </div>
        ) : (
          recentDrivers.map(req => {
            const driver = { id: req.driverId, name: req.driverName, avatar: req.driverAvatar, rating: req.driverRating || 0 };
            const remaining = timeRemaining(req.tripDatetime);
            if (!remaining) return null;
            return (
              <div key={req.id} style={{ margin: "16px 16px 0", padding: 16, background: "var(--card)", borderRadius: 18, border: "1.5px solid var(--border)", boxShadow: "var(--shadow)" }}>
                {/* Driver info */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                  <Avatar user={driver} size={48}/>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>{driver.name}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#f5a623"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      {driver.rating ? driver.rating.toFixed(1) : "—"}
                    </div>
                  </div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 8, background: "rgba(245,166,35,.1)", color: "#f5a623", fontFamily: "Syne", fontWeight: 700, fontSize: 11, border: "1px solid rgba(245,166,35,.2)" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {remaining}
                  </span>
                </div>

                {/* Route */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--paper)", borderRadius: 12, marginBottom: 10 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)" }}/>
                    <span style={{ width: 1.5, height: 12, background: "var(--border)" }}/>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--ink)", opacity: .5 }}/>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{req.tripFrom} → {req.tripTo}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>Completed {completedAgo(req.tripDatetime)}</div>
                  </div>
                </div>

                {/* Contact button */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 12, color: "var(--muted)", display: "flex", alignItems: "center", gap: 4 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {new Date(req.tripDatetime).toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <button onClick={() => contactDriver(req)} disabled={!!creating} style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
                    borderRadius: 10, background: "var(--accent)", color: "#fff",
                    fontFamily: "Syne", fontWeight: 700, fontSize: 12,
                    border: "none", cursor: creating === req.id ? "wait" : "pointer",
                    opacity: creating && creating !== req.id ? 0.5 : 1,
                    transition: "all .15s",
                  }}>
                    {creating === req.id ? (
                      <span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                    )}
                    {req.existingChatId ? "Open Chat" : "Contact Driver"}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ForgotItem;
